import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'

import Page from '../index'
import InfoBlock from '../../features/components/InfoBlock'
import DigitalRight from './DigitalRight'
import OnsiteRightContent from './OnsiteRightContent'
import SocialPosts from './SocialPosts'

import usePartnershipManage from '../../features/hooks/usePartnershipManage'

import { getRight, clearRight } from '../../modules/actions/rights'
import {
  rightSelector,
  rightErrorSelector,
  rightWasLoadedSelector,
  rightIsLoadingSelector
} from '../../modules/selectors/rights'
import { selectedPartnershipIdSelector } from '../../modules/selectors/partnerships'

import { ROUTE_PARAMS, ROUTES } from '../../constants/routes'
import {
  RIGHT_TYPE_DIGITAL,
  RIGHT_TYPE_ONSITE,
  RIGHT_TYPE_POSTS
} from '../../forms/Multiplatform/Partnerships/RightForms/fields'

import useSummaryPagesStyles from '../../styles/common/summaryPages'

const RightsSummary = () => {
  const summaryPagesClasses = useSummaryPagesStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { [ROUTE_PARAMS.rightId]: rightId } = useParams()

  const right = useSelector(rightSelector)
  const rightIsLoading = useSelector(rightIsLoadingSelector)
  const rightError = useSelector(rightErrorSelector)
  const rightWasLoaded = useSelector(rightWasLoadedSelector)
  const selectedPartnershipId = useSelector(selectedPartnershipIdSelector)

  const { handlePartnershipChange } = usePartnershipManage()

  const { name: rightName, partnership_id: rightPartnershipId, right_type: rightType } = right

  const showInitialLoading = rightIsLoading && !(rightWasLoaded || rightError)

  const rightSummaryBreadcrumbsTitle = useMemo(() => {
    switch (rightType) {
      case RIGHT_TYPE_ONSITE:
        return t('Onsite Campaigns')
      case RIGHT_TYPE_POSTS:
        return t('Social Posts')
      case RIGHT_TYPE_DIGITAL:
      default:
        return t('Digital Campaigns')
    }
  }, [rightType, t])

  const breadcrumbs = useMemo(
    () => [
      {
        title: 'Partnership Dashboard',
        url: ROUTES.partnerships
      },
      {
        title: rightSummaryBreadcrumbsTitle
      }
    ],
    [rightSummaryBreadcrumbsTitle]
  )

  const renderPageContent = useCallback(() => {
    if (
      rightWasLoaded &&
      rightType !== RIGHT_TYPE_DIGITAL &&
      rightType !== RIGHT_TYPE_ONSITE &&
      rightType !== RIGHT_TYPE_POSTS
    ) {
      return (
        <InfoBlock
          title={t('Sorry, you can view this page only for the digital, posts or onsight rights')}
          centered
          greyDescription
        >
          {t('You may try accessing different right with correct type')}
        </InfoBlock>
      )
    } else if (rightError) {
      return (
        <InfoBlock title={t('Sorry, we can’t find that right')} centered greyDescription>
          {t('You might not have access to it, or it may have been deleted')}
        </InfoBlock>
      )
    } else if (rightType === RIGHT_TYPE_ONSITE) {
      return <OnsiteRightContent />
    } else if (rightType === RIGHT_TYPE_DIGITAL) {
      return <DigitalRight />
    } else if (rightType === RIGHT_TYPE_POSTS) {
      return <SocialPosts rightId={rightId} />
    } else {
      return null
    }
  }, [t, rightWasLoaded, rightError, rightType, rightId])

  // there can be case when user open right page, when not related partnership is selected
  // if that happens, we manually change selected partnership to one from the right data
  useEffect(() => {
    if (rightPartnershipId && selectedPartnershipId && String(rightPartnershipId) !== selectedPartnershipId) {
      handlePartnershipChange({ value: rightPartnershipId, keepUserOnCurrentPage: true })
    }
  }, [rightPartnershipId, selectedPartnershipId, handlePartnershipChange])

  useEffect(() => {
    if (rightId) {
      dispatch(getRight(rightId))
    }

    return () => {
      dispatch(clearRight())
    }
  }, [dispatch, rightId])

  return (
    <Page helmetTitle="Rights" breadcrumbs={breadcrumbs} additionalPartnershipInfo={rightWasLoaded ? rightName : ''}>
      {showInitialLoading && (
        <h2 className={summaryPagesClasses.welcomeMessage}>
          <Skeleton style={{ maxWidth: 450 }} />
        </h2>
      )}
      {renderPageContent()}
    </Page>
  )
}

export default RightsSummary
